import { useEffect, useState } from 'react';
import { api } from '../api';
import type { TimeEntry } from '../api';
import { Link, navigate, useQuery } from '../router';
import { useSession } from '../session';
import { useData } from '../useData';
import { Shell } from '../components/Shell';
import { Empty, hoursOf, Loading, when } from '../components/bits';
import { TagSelect } from '../components/TagSelect';

interface Filters {
  workspaceId?: number;
  projectId?: number;
  userId?: number;
  from?: string;
  to?: string;
}

/** The time entries manager: everything logged, narrowed by project, person
 *  and dates, with the total for whatever is on screen.
 *
 *  The filters live in the URL, so a view is a link. That is what lets a day
 *  on somebody's overview open as "that person, that day" here, the way the
 *  Drupal block's day links did. */
export function Time() {
  const { t, locale, user } = useSession();
  const query = useQuery();

  const [filters, setFilters] = useState<Filters>(() => fromQuery(query));

  // A navigation from elsewhere carries its own filters.
  const queryKey = query.toString();
  useEffect(() => {
    setFilters(fromQuery(new URLSearchParams(queryKey)));
  }, [queryKey]);

  // Only the studio logs hours, so only the studio can be filtered by.
  const people = useData(() => api.people(true), []);
  const workspaces = useData(() => api.workspaces(), []);
  // Narrowing the client narrows the project list with it: there are 289
  // projects, and a select of all of them is not a filter, it is a haystack.
  const projects = useData(
    () => api.projects({ workspaceId: filters.workspaceId }),
    [filters.workspaceId],
  );
  const entries = useData(
    () => api.time({ ...filters, limit: 500 }),
    [filters.workspaceId, filters.projectId, filters.userId, filters.from, filters.to],
  );

  const total = entries.data?.reduce((sum, e) => sum + e.minutes, 0) ?? 0;

  const apply = (next: Filters) => {
    setFilters(next);
    navigate(`/time${toQuery(next)}`, true);
  };

  const narrowed = Object.values(filters).some((v) => v !== undefined && v !== '');

  return (
    <Shell>
      <div className="page-head">
        <div className="grow">
          <p className="kicker">
            {narrowed ? t.entries(entries.data?.length ?? 0) : t.noFilters}
          </p>
          <h1>{t.time}</h1>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ margin: 0, fontSize: 'var(--fs-heading)' }}>{hoursOf(total)}h</p>
          {/* Whatever is on screen is what comes out, which is how you get a
              month of one client's hours: set the client and the dates. */}
          <a className="link primary" href={api.timeCsvUrl({ ...filters })}>{t.export} &gt;</a>
        </div>
      </div>

      <div className="form-grid filter-grid" style={{ marginBottom: 'var(--sp-5)' }}>
        <div>
          <label htmlFor="f-client">{t.client}</label>
          <TagSelect
            id="f-client"
            value={filters.workspaceId}
            placeholder={t.all}
            options={(workspaces.data ?? []).map((w) => ({ value: w.id, label: w.name }))}
            onChange={(workspaceId) => apply({
              ...filters,
              workspaceId,
              // A project from the old client would contradict the new one.
              projectId: undefined,
            })}
          />
        </div>
        <div>
          <label htmlFor="f-project">{t.project}</label>
          <TagSelect
            id="f-project"
            value={filters.projectId}
            placeholder={t.all}
            options={(projects.data ?? []).map((p) => ({
              value: p.id,
              label: p.title,
              hint: filters.workspaceId ? undefined : p.workspaceName,
            }))}
            onChange={(projectId) => apply({ ...filters, projectId })}
          />
        </div>
        <div>
          <label htmlFor="f-person">{t.person}</label>
          <TagSelect
            id="f-person"
            value={filters.userId}
            placeholder={t.all}
            options={(people.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
            onChange={(userId) => apply({ ...filters, userId })}
          />
        </div>
        <div>
          <label htmlFor="f-from">{t.from}</label>
          <input
            id="f-from"
            type="date"
            value={filters.from ?? ''}
            onChange={(e) => apply({ ...filters, from: e.target.value || undefined })}
          />
        </div>
        <div>
          <label htmlFor="f-to">{t.to}</label>
          <input
            id="f-to"
            type="date"
            value={filters.to ?? ''}
            onChange={(e) => apply({ ...filters, to: e.target.value || undefined })}
          />
        </div>
        <div>
          <button onClick={() => apply({})} disabled={!narrowed}>{t.clear}</button>
        </div>
      </div>

      {entries.loading && !entries.data ? <Loading /> : null}
      {entries.data?.length === 0 ? <Empty>{t.noTime}</Empty> : null}
      {entries.data && entries.data.length > 0 && (
        <Entries rows={entries.data} locale={locale} canRemove={user?.id} onRemoved={entries.reload} />
      )}
    </Shell>
  );
}

function Entries({
  rows, locale, canRemove, onRemoved,
}: {
  rows: TimeEntry[];
  locale: string;
  canRemove?: number;
  onRemoved: () => void;
}) {
  const { t, user } = useSession();

  // The days are grouped, because a fortnight of one person's work reads as a
  // handful of days and not as sixty rows.
  const days: { day: string; rows: TimeEntry[]; minutes: number }[] = [];
  for (const row of rows) {
    const key = row.workedOn.slice(0, 10);
    const last = days[days.length - 1];
    if (last && last.day === key) {
      last.rows.push(row);
      last.minutes += row.minutes;
    } else {
      days.push({ day: key, rows: [row], minutes: row.minutes });
    }
  }

  const [editing, setEditing] = useState<number | null>(null);

  const remove = async (id: number) => {
    if (!window.confirm(t.confirmRemove)) return;
    await api.deleteTime(id);
    onRemoved();
  };

  // An entry is held only while its document is being settled: a draft is a
  // figure somebody is assembling, an approved one is a figure somebody has
  // signed off. An issued document froze its own copy of the numbers, so the
  // hours underneath can still be corrected — which is most of them, because
  // everything migrated sits on one historical document per client.
  const settling = (e: TimeEntry) =>
    e.documentState === 'open';
  const mine = (e: TimeEntry) =>
    !settling(e) && (e.userId === canRemove || (user?.isAdmin ?? false));

  return (
    <table>
      <thead>
        <tr>
          <th className="date">{t.date}</th>
          <th>{t.project}</th>
          <th>{t.what}</th>
          <th>{t.person}</th>
          <th className="num">{t.hours}</th>
          <th className="actions" />
        </tr>
      </thead>
      {days.map((d) => (
        <tbody key={d.day}>
          {d.rows.map((e, i) => (
            editing === e.id ? (
              <tr key={e.id}>
                <td colSpan={6}>
                  <EditEntry
                    entry={e}
                    onCancel={() => setEditing(null)}
                    onSaved={() => { setEditing(null); onRemoved(); }}
                  />
                </td>
              </tr>
            ) : (
              <tr key={e.id}>
                <td className="muted date">{i === 0 ? when(e.workedOn, locale) : ''}</td>
                {/* A run of entries on the same project says the project once,
                    the way the date does. Repeating it turned a day's work
                    into a wall of the same sixty characters. */}
                <td>
                  {sameProject(d.rows, i) ? '' : (
                    <Link to={`/projects/${e.projectId}`}>
                      {e.workspaceName} · {e.projectTitle}
                    </Link>
                  )}
                </td>
                <td>{e.title ?? ''}</td>
                <td className="muted">{e.userName ?? ''}</td>
                <td className="num">{hoursOf(e.minutes)}</td>
                {/* This column says what you can do, and why not when you
                    cannot. A closed document says nothing: the hours can be
                    corrected like any others, and 9,330 of them sit on one, so
                    a word there would be noise on almost every row. The
                    documents themselves are on the billing page. */}
                <td className="actions">
                  {mine(e) ? (
                    <>
                      <button onClick={() => setEditing(e.id)}>{t.edit}</button>
                      <button onClick={() => remove(e.id)} title={t.remove}>&times;</button>
                    </>
                  ) : settling(e) ? (
                    <Link
                      to={`/documents/${e.documentId}`}
                      className="muted"
                      title={t.onADocumentHint}
                    >
                      {t.onADocument}
                    </Link>
                  ) : null}
                </td>
              </tr>
            )
          ))}
        </tbody>
      ))}
    </table>
  );
}

/** Whether this row's project is the one above it, within the same day. */
function sameProject(rows: TimeEntry[], i: number): boolean {
  return i > 0 && rows[i - 1]!.projectId === rows[i]!.projectId;
}

/** Correcting an entry: the day, the hours and what it was for. The project
 *  is not editable here — time logged against the wrong project is a different
 *  mistake, and deleting and re-logging says so more clearly than a silent
 *  move between two clients' bills. */
function EditEntry({
  entry, onCancel, onSaved,
}: {
  entry: TimeEntry;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useSession();
  const [form, setForm] = useState({
    workedOn: entry.workedOn.slice(0, 10),
    hours: (entry.minutes / 60).toFixed(2).replace(/\.00$/, ''),
    title: entry.title ?? '',
    notes: entry.notes ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    const minutes = Math.round(parseFloat(form.hours.replace(',', '.')) * 60);
    if (!Number.isFinite(minutes) || minutes <= 0) return;

    setBusy(true);
    setProblem(null);
    try {
      await api.updateTime(entry.id, {
        workedOn: form.workedOn,
        minutes,
        title: form.title,
        notes: form.notes,
      });
      onSaved();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="card stack">
      <p className="kicker" style={{ margin: 0 }}>{entry.workspaceName} · {entry.projectTitle}</p>
      <div className="row">
        <div style={{ maxWidth: 160 }}>
          <label htmlFor={`e-when-${entry.id}`}>{t.date}</label>
          <input id={`e-when-${entry.id}`} type="date" value={form.workedOn}
            onChange={(e) => setForm({ ...form, workedOn: e.target.value })} />
        </div>
        <div style={{ maxWidth: 90 }}>
          <label htmlFor={`e-hours-${entry.id}`}>{t.hours}</label>
          <input id={`e-hours-${entry.id}`} inputMode="decimal" value={form.hours}
            onChange={(e) => setForm({ ...form, hours: e.target.value })} />
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <label htmlFor={`e-what-${entry.id}`}>{t.what}</label>
          <input id={`e-what-${entry.id}`} value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
      </div>
      {problem && <p className="error" role="alert">{problem}</p>}
      <div className="row">
        <button className="primary" type="submit" disabled={busy}>{t.save} &gt;</button>
        <button type="button" onClick={onCancel}>{t.cancel}</button>
      </div>
    </form>
  );
}

function fromQuery(q: URLSearchParams): Filters {
  return {
    workspaceId: num(q.get('workspaceId')),
    projectId: num(q.get('projectId')),
    userId: num(q.get('userId')),
    from: q.get('from') ?? undefined,
    to: q.get('to') ?? undefined,
  };
}

function toQuery(f: Filters): string {
  const q = new URLSearchParams();
  if (f.workspaceId) q.set('workspaceId', String(f.workspaceId));
  if (f.projectId) q.set('projectId', String(f.projectId));
  if (f.userId) q.set('userId', String(f.userId));
  if (f.from) q.set('from', f.from);
  if (f.to) q.set('to', f.to);
  const s = q.toString();
  return s ? `?${s}` : '';
}

function num(v: string | null): number | undefined {
  if (!v) return undefined;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}
